import React, { useMemo } from "react";
import { AgCharts } from "ag-charts-react";
import { BRAND, CHART_COLORS, ChartEmptyState } from "./DashboardUi";

/**
 * Donut chart + ranked legend list.
 *
 * The legend is rendered in React (not by AG Charts) so long article names
 * truncate cleanly, every row shows its count and share, and colours always
 * line up with the chart slices.
 *
 * Props:
 *   data   – [{ article_title, count }]
 *   height – chart height in px (default 240)
 */
const BarChart = ({ data = [], height = 240 }) => {
  const chartData = useMemo(
    () =>
      (data || [])
        .filter((item) => item?.count)
        .map((item) => ({ count: item.count, article: item.article_title })),
    [data],
  );

  const total = useMemo(
    () => chartData.reduce((sum, d) => sum + d.count, 0),
    [chartData],
  );

  const options = useMemo(
    () => ({
      data: chartData,
      height,
      background: { fill: "transparent" },
      padding: { top: 8, right: 8, bottom: 8, left: 8 },
      legend: { enabled: false },
      series: [
        {
          type: "donut",
          angleKey: "count",
          calloutLabelKey: "article",
          calloutLabel: { enabled: false },
          sectorLabel: { enabled: false },
          innerRadiusRatio: 0.65,
          fills: CHART_COLORS,
          strokes: ["#ffffff"],
          strokeWidth: 2,
          innerLabels: [
            {
              text: String(total),
              fontSize: 28,
              fontWeight: "bold",
              color: BRAND.navy,
            },
            { text: "Total", fontSize: 12, color: "#94A3B8", margin: 4 },
          ],
          tooltip: {
            renderer: ({ datum }) => ({
              title: datum.article,
              content: `${datum.count} (${Math.round(
                (datum.count / total) * 100,
              )}%)`,
            }),
          },
        },
      ],
    }),
    [chartData, total, height],
  );

  if (chartData.length === 0) {
    return (
      <ChartEmptyState
        title="No values to chart"
        message="All counts are zero for this check."
        height={height}
      />
    );
  }

  return (
    <div className="flex w-full flex-col items-center gap-4 sm:flex-row">
      <div className="w-full sm:w-1/2">
        <AgCharts options={options} />
      </div>

      <ul
        className="w-full space-y-1 overflow-y-auto pr-1 sm:w-1/2"
        style={{ maxHeight: height }}
      >
        {chartData.map((d, i) => (
          <li
            key={`${d.article}-${i}`}
            className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm transition-colors hover:bg-slate-50"
          >
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
            />
            <span
              className="min-w-0 flex-1 truncate text-slate-600"
              title={d.article}
            >
              {d.article}
            </span>
            <span className="font-semibold tabular-nums text-slate-800">
              {d.count}
            </span>
            <span className="w-10 text-right text-xs tabular-nums text-slate-400">
              {Math.round((d.count / total) * 100)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default BarChart;
